import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Select, Label, Badge, Table, Td, Modal } from "../components/UI";
import { Plus, Pencil, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { OnCallCard } from "./StaffPanels";
import { PrescriberFields, PrescriberChip } from "./PrescriberFields";

const ROLES = [["admin", "Administrator"], ["clinician", "Clinician"], ["front_desk", "Front Desk"], ["billing", "Billing"]];
const EMPTY = { email: "", password: "", first_name: "", last_name: "", role: "clinician", title: "", phone: "", requires_cosign: false, supervisor_id: "",
  is_prescriber: false, prescriber_credentials: "", prescriber_npi: "", prescriber_dea: "", prescriber_license: "" };
const RX_KEYS = ["is_prescriber", "prescriber_credentials", "prescriber_npi", "prescriber_dea", "prescriber_license"];

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  const caseloadOf = (uid) => clients.filter((c) => c.clinician_id === uid).length;

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const { email, password, ...rest } = form;
        await api.put(`/users/${editId}`, rest);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); setEditId(null); setForm(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ ...EMPTY, first_name: u.first_name, last_name: u.last_name, role: u.role, title: u.title || "",
      phone: u.phone || "", requires_cosign: !!u.requires_cosign, supervisor_id: u.supervisor_id || "",
      ...Object.fromEntries(RX_KEYS.map((k) => [k, k === "is_prescriber" ? !!u[k] : u[k] || ""])) });
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const supervisors = users.filter((u) => !u.requires_cosign && u.status === "active" && ["admin", "clinician"].includes(u.role) && u.id !== editId);
  const supName = (id) => { const s = users.find((u) => u.id === id); return s ? `${s.first_name} ${s.last_name}` : "-"; };

  return (
    <div>
      <PageHeader title="Users" subtitle="Staff accounts, roles & co-signature supervision"
        action={isAdmin && <Btn data-testid="add-user-btn" onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }}><Plus size={16} /> New User</Btn>} />
      <OnCallCard users={users} isAdmin={isAdmin} />
      <Card>
        <Table headers={["Name", "Email", "Role", "Title", "Caseload", "Co-Sign", "Prescriber", "2FA", "Status", ""]}>
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#FDFCF8]">
              <Td className="font-semibold">
                {(isAdmin || u.id === user?.id)
                  ? <Link to={`/staff/${u.id}`} data-testid={`user-name-link-${u.id}`} className="text-[#3E2723] hover:underline">{u.first_name} {u.last_name}</Link>
                  : <>{u.first_name} {u.last_name}</>}
              </Td>
              <Td>{u.email}</Td>
              <Td className="capitalize">{String(u.role).replace("_", " ")}</Td>
              <Td>{u.title}</Td>
              <Td>
                {(isAdmin || u.id === user?.id) ? (
                  <Link to={`/staff/${u.id}`} data-testid={`user-caseload-${u.id}`}
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${caseloadOf(u.id) > 0 ? "bg-[#F5F2EB] text-[#4E342E] hover:bg-[#EDE7DC]" : "text-[#84675B]"}`}>
                    {caseloadOf(u.id) > 0 ? `${caseloadOf(u.id)} clients` : "-"}
                  </Link>
                ) : <span className="text-xs text-[#84675B]">{caseloadOf(u.id) || "-"}</span>}
              </Td>
              <Td>
                {u.requires_cosign ? (
                  <span data-testid={`user-cosign-${u.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2.5 py-0.5 text-xs font-semibold text-[#B84A00]">
                    <UserCheck size={11} /> {supName(u.supervisor_id)}
                  </span>
                ) : <span className="text-xs text-[#84675B]">-</span>}
              </Td>
              <Td><PrescriberChip u={u} /></Td>
              <Td>{u.totp_enabled ? <Badge value="active" /> : <span className="text-xs text-[#84675B]">Off</span>}</Td>
              <Td><Badge value={u.status} /></Td>
              <Td>
                <div className="flex gap-2 justify-end">
                  {isAdmin && (
                    <button type="button" data-testid={`edit-user-${u.id}`} aria-label="Edit user" onClick={() => openEdit(u)}
                      className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                  )}
                  {isAdmin && u.id !== user.id && (
                    <Btn variant="outline" data-testid={`toggle-user-${u.id}`} className="px-3 py-1 text-xs" onClick={() => toggleStatus(u)}>
                      {u.status === "active" ? "Deactivate" : "Activate"}
                    </Btn>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? "Edit User" : "New User"} wide>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <div><Label>First name</Label><Input data-testid="user-first-name-input" value={form.first_name} onChange={set("first_name")} required /></div>
          <div><Label>Last name</Label><Input data-testid="user-last-name-input" value={form.last_name} onChange={set("last_name")} required /></div>
          {!editId && (
            <>
              <div><Label>Email</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={set("email")} required /></div>
              <div><Label>Temporary password</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={set("password")} required minLength={8} /></div>
            </>
          )}
          <div><Label>Role</Label>
            <Select data-testid="user-role-select" value={form.role} onChange={set("role")}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </div>
          <div><Label>Title</Label><Input value={form.title} onChange={set("title")} placeholder="e.g. LCSW" /></div>
          <div className="col-span-2"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
          <div className="col-span-2 rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-[#3E2723] cursor-pointer">
              <input type="checkbox" data-testid="user-requires-cosign" checked={form.requires_cosign}
                onChange={(e) => setForm({ ...form, requires_cosign: e.target.checked, supervisor_id: e.target.checked ? form.supervisor_id : "" })}
                className="h-4 w-4 accent-[#4E342E]" />
              Requires co-signature (intern / associate-level clinician)
            </label>
            <p className="mt-1 text-xs text-[#7B6157]">Progress notes signed by this user stay locked as "Pending Co-Sign" until the supervisor countersigns.</p>
            {form.requires_cosign && (
              <div className="mt-3">
                <Label>Supervising clinician</Label>
                <Select data-testid="user-supervisor-select" value={form.supervisor_id} onChange={set("supervisor_id")} required>
                  <option value="">Select supervisor…</option>
                  {supervisors.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.title || s.role})</option>)}
                </Select>
              </div>
            )}
          </div>
          <PrescriberFields form={form} setForm={setForm} />
          <div className="col-span-2 flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => { setOpen(false); setEditId(null); }}>Cancel</Btn>
            <Btn data-testid="user-save-btn" type="submit">{editId ? "Save Changes" : "Create User"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
